import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Chips, useDeviceType } from '@cian/ui-kit';
import { useHistory, useLocation } from 'react-router-dom';

import { Scrolling, ScrollingWithControls } from '../../components/Scrolling';
import { selectTags } from '../../selectors/tags/selectTags';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import { getTags, getTagsReset } from '../../actions/tags';
import { selectCurrentPageMeta } from '../../selectors/pagesMeta';
import { ERequestStatus } from '../../types/requestStatus';
import { Skeleton } from '../../components/Skeleton';
import { prepareQsTagName, transformPageType } from './utils';
import { useSensitiveParams, useValidTag } from '../../utils';
import { EType } from '../../repositories/journal/entities/page_info/PageInfoSchema';
import * as s from './TagScrollingContainer.css';

/** Скроллинг тэгов для мобилки и десктопа */
export const TagScrollingContainer = () => {
  const { pathname } = useLocation();
  const dispatch = useDispatch<TThunkDispatch>();
  const { type: pageType, rubricId } = useSelector((state: IApplicationState) =>
    selectCurrentPageMeta(state, pathname),
  );
  const deviceType = useDeviceType();
  const isPhone = () => deviceType === 'phone';
  const isTablet = () => deviceType === 'tablet';
  const { status: tagsStatus, items: tags } = useSelector(selectTags);
  const withTag = useValidTag('with-tag');
  const history = useHistory();
  const key = useSensitiveParams(['rubricId', 'pageType']);
  const [prevKey, setPrevKey] = useState(key);

  /** Запрашивает набор тэгов, если их нет */
  useEffect(() => {
    if (tagsStatus !== ERequestStatus.Initial && key === prevKey) {
      return;
    }

    dispatch(getTags({ pageType: transformPageType(pageType), rubricId: rubricId || 0 })).finally();
    setPrevKey(key);
  }, [dispatch, key, pageType, prevKey, rubricId, tagsStatus]);

  /** Очищает текущий набор тэгов */
  useEffect(() => {
    return () => {
      dispatch(getTagsReset());
    };
  }, [dispatch]);

  /** Отправляет на страницу поиска по тэгам */
  const handleClick = useCallback(
    (tagName: string) => {
      let pathname = '/magazine/';
      const tagQsName = prepareQsTagName(pageType);

      if (!tagQsName) {
        return;
      }

      const searchParams = new URLSearchParams();

      if (pageType === EType.News) {
        pathname = '/novosti/';
      } else if (pageType === EType.Articles) {
        pathname = '/stati/';
      } else if (pageType === EType.Blogs) {
        pathname = '/blogs/';
      } else if (pageType === EType.Questions) {
        pathname = '/forum-rieltorov/';
      }

      searchParams.set(tagQsName, tagName);
      history.push(`${pathname}?${searchParams.toString()}`);
    },
    [history, pageType],
  );

  const chipsOptions = tags.map(({ name, id }) => ({ value: name, label: `#${name}`, dataTestId: `chip-${id}` }));
  const chipsValue = withTag ? [withTag] : [];

  const chipsElement = (
    <div className={s['chips-row']}>
      <Chips
        options={chipsOptions}
        value={chipsValue}
        selectType="single"
        size={isPhone() ? 'M' : 'S'}
        onChipClick={handleClick}
        padded={false}
        scrollable
      />
    </div>
  );

  return (
    <div className={s['wrapper']} data-web-ui-test-id="TagScrollingContainer">
      {tagsStatus === ERequestStatus.Loading && (
        <div className={s['skeleton-wrapper']}>
          {(isPhone()
            ? ['40%', '20%', '30%']
            : isTablet()
              ? ['20%', '10%', '20%', '10%', '20%']
              : ['20%', '10%', '15%', '10%', '20%', '13%', '10%']
          ).map((percent, index) => (
            <Skeleton key={index} width={percent} height={isPhone() ? 40 : 28} />
          ))}
        </div>
      )}

      {tagsStatus === ERequestStatus.Succeed && tags.length ? (
        isPhone() ? (
          <Scrolling>{chipsElement}</Scrolling>
        ) : (
          <ScrollingWithControls>{chipsElement}</ScrollingWithControls>
        )
      ) : null}
    </div>
  );
};
